import React, { useMemo, useState } from 'react';
import { Edit2, PowerOff, Eye, RotateCcw, LinkIcon, Copy, RefreshCw, Users, AlertTriangle } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { ConfirmDialog } from '../../../../components/shared/ConfirmDialog';
import { Avatar } from '../../shared/Avatar';
import { formatRelativeTime } from '../../../../utils/formatters';
import { isLearnerAccount, getTeacherClassrooms, getLearnerExistingClassrooms, type Account, type AccountsState } from '../../../../accounts';
import { generateUniqueLinkCode } from '../../../../accounts';
import { getMasteryBand } from '../../../shared/masteryBands';
import { ROLE_CONFIG } from './types';
import type { UserRow } from './types';
interface UserDetailPanelProps {
  user: UserRow;
  isEditing: boolean;
  selectedMastery: Array<{
    subjectId: string;
    label: string;
    avg: number;
    count: number;
  }>;
  accounts: AccountsState | undefined;
  store: {
    updateAccountNameById: (id: string, name: string) => void;
    updateAccountEmail: (id: string, email: string) => void;
    updateTeacherDetails: (id: string, details: {
      schoolName: string;
    }) => void;
    updateLearnerDetails: (id: string, details: {
      grade: number;
    }) => void;
    deleteAccount: (id: string) => void;
    resetLearnerProgress: (id: string) => void;
    setLearnerLinkCode: (id: string, code: string) => void;
  };
  onNavigateToLearner?: (learnerId: string) => void;
  onClose: () => void;
  onStartEdit: (user: UserRow) => void;
  onStopEdit: () => void;
  onUserUpdate: (updater: (prev: UserRow | null) => UserRow | null) => void;
}
export function UserDetailPanel({
  user,
  isEditing,
  selectedMastery,
  accounts,
  store,
  onNavigateToLearner,
  onClose,
  onStartEdit,
  onStopEdit,
  onUserUpdate
}: UserDetailPanelProps) {
  const [resetConfirm, setResetConfirm] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  // Edit form state
  const [editUserName, setEditUserName] = useState(user.name);
  const [editUserEmail, setEditUserEmail] = useState(user.email !== '—' ? user.email : '');
  // Role is intentionally not editable from this panel — kept as a plain
  // derived value rather than useState to avoid the previous "misleading
  // const useState" pattern (audit V10 / P2-2).
  const editUserRole = user.role;
  const [editUserOrganization, setEditUserOrganization] = useState(user.organization !== '—' ? user.organization : '');
  const [editUserGrade, setEditUserGrade] = useState(user.account && isLearnerAccount(user.account) ? String(user.account.grade || 6) : '6');
  // Grade-mismatch warning when editing learner grade
  const gradeWarnings = useMemo(() => {
    if (editUserRole !== 'learner' || !accounts) return [];
    const newGrade = parseInt(editUserGrade, 10);
    const existing = getLearnerExistingClassrooms(accounts, user.id);
    const warnings: string[] = [];
    for (const {
      classroom,
      teacherName
    } of existing) {
      if (classroom.groupLevel && classroom.groupLevel !== newGrade) {
        warnings.push(`${user.name} zit in "${classroom.name}" (groep ${classroom.groupLevel}) van ${teacherName}. De nieuwe groep is ${newGrade}.`);
      }
    }
    return warnings;
  }, [editUserRole, editUserGrade, accounts, user.id, user.name]);
  const handleEditUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUserName.trim()) return;
    if (editUserName !== user.name) {
      store.updateAccountNameById(user.id, editUserName.trim());
    }
    if (editUserEmail !== (user.email !== '—' ? user.email : '')) {
      store.updateAccountEmail(user.id, editUserEmail.trim());
    }
    if (editUserRole === 'teacher' && editUserOrganization !== (user.organization !== '—' ? user.organization : '')) {
      store.updateTeacherDetails(user.id, {
        schoolName: editUserOrganization.trim()
      });
    }
    if (editUserRole === 'learner' && user.account && isLearnerAccount(user.account)) {
      const newGrade = parseInt(editUserGrade, 10);
      const currentGrade = user.account.grade || 6;
      if (newGrade !== currentGrade) {
        store.updateLearnerDetails(user.id, {
          grade: newGrade
        });
      }
    }
    onUserUpdate(prev => prev ? {
      ...prev,
      name: editUserName.trim(),
      email: editUserEmail.trim() || '—',
      organization: editUserOrganization.trim() || '—'
    } : null);
    onStopEdit();
  };
  if (isEditing) {
    return <div data-id="element-6593">
        <form onSubmit={handleEditUser} className="space-y-4" data-id="element-6594">
          <Input label="Naam" type="text" required value={editUserName} onChange={e => setEditUserName(e.target.value)} data-id="element-6595" />
          <Input label="E-mailadres" type="email" value={editUserEmail} onChange={e => setEditUserEmail(e.target.value)} data-id="element-6596" />
          {editUserRole === 'teacher' && <Input label="School" type="text" value={editUserOrganization} onChange={e => setEditUserOrganization(e.target.value)} data-id="element-6597" />}
          {editUserRole === 'learner' && <>
              <Select label="Groep" value={editUserGrade} onChange={e => setEditUserGrade(e.target.value)} options={[{
            value: '6',
            label: 'Groep 6'
          }, {
            value: '7',
            label: 'Groep 7'
          }, {
            value: '8',
            label: 'Groep 8'
          }]} data-id="element-6598" />
              {gradeWarnings.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1" data-id="element-6599">
                  <div className="flex items-center gap-1.5 text-amber-800 text-xs font-semibold" data-id="element-6600">
                    <AlertTriangle className="w-3.5 h-3.5" data-id="element-6601" />
                    Groep-mismatch
                  </div>
                  {gradeWarnings.map((w, i) => <p key={i} className="text-xs text-amber-700" data-id="element-6602">
                      {w}
                    </p>)}
                </div>}
            </>}
          <div className="pt-4 flex justify-end gap-2 border-t border-gray-100" data-id="element-6603">
            <Button type="button" variant="secondary" onClick={onStopEdit} data-id="element-6604">
              Annuleren
            </Button>
            <Button type="submit" data-id="element-6605">Opslaan</Button>
          </div>
        </form>
      </div>;
  }
  return <div className="space-y-6" data-id="element-6606">
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray-100" data-id="element-6607">
        <Avatar name={user.name} size="md" data-id="element-6608" />
        <div data-id="element-6609">
          <div className="font-semibold text-gray-900" data-id="element-6610">{user.name}</div>
          <div className="text-sm text-gray-500" data-id="element-6611">{user.organization}</div>
          <div className="text-xs text-gray-400 mt-0.5" data-id="element-6612">
            Laatste activiteit: {formatRelativeTime(user.lastActive, true)}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3" data-id="element-6613">
        <div className="bg-gray-50 rounded-xl p-4" data-id="element-6614">
          <div className="text-xs text-gray-500 mb-1" data-id="element-6615">Rol</div>
          <Badge variant={ROLE_CONFIG[user.role].variant} size="sm" data-id="element-6616">
            {ROLE_CONFIG[user.role].label}
          </Badge>
        </div>
        {user.role === 'learner' && <>
            <div className="bg-emerald-50 rounded-lg p-3" data-id="element-6617">
              <div className="text-2xl font-bold text-emerald-700" data-id="element-6618">
                {user.avgMastery}%
              </div>
              <div className="text-xs text-emerald-600" data-id="element-6619">Gem. beheersing</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3" data-id="element-6620">
              <div className="text-2xl font-bold text-blue-700" data-id="element-6621">
                {user.sessionCount}
              </div>
              <div className="text-xs text-blue-600" data-id="element-6622">Sessies</div>
            </div>
            <div className="bg-violet-50 rounded-xl p-4" data-id="element-6623">
              <div className="text-xs text-gray-500 mb-1" data-id="element-6624">XP Totaal</div>
              <div className="text-xl font-bold text-violet-600" data-id="element-6625">
                {user.xp.toLocaleString('nl-NL')}
              </div>
            </div>
          </>}
        {user.role === 'teacher' && <div className="bg-indigo-50 rounded-lg p-3" data-id="element-6626">
            <div className="text-2xl font-bold text-indigo-700" data-id="element-6627">
              {user.linkedCount}
            </div>
            <div className="text-xs text-indigo-600" data-id="element-6628">Leerlingen</div>
          </div>}
        {user.role === 'parent' && <div className="bg-rose-50 rounded-lg p-3" data-id="element-6629">
            <div className="text-2xl font-bold text-rose-700" data-id="element-6630">
              {user.linkedCount}
            </div>
            <div className="text-xs text-rose-600" data-id="element-6631">Kinderen</div>
          </div>}
      </div>

      {/* Teacher Classrooms with JoinCodes */}
      {user.role === 'teacher' && accounts && (() => {
      const classrooms = getTeacherClassrooms(accounts, user.id);
      if (classrooms.length === 0) return null;
      return <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-6632">
              <h3 className="text-sm font-semibold text-indigo-900 flex items-center gap-1.5 mb-3" data-id="element-6633">
                <Users className="w-3.5 h-3.5" data-id="element-6634" /> Klassen & Koppelcodes
              </h3>
              <div className="space-y-2" data-id="element-6635">
                {classrooms.map(c => <div key={c.id} className="bg-white rounded-lg px-3 py-2 border border-indigo-200" data-id="element-6636">
                    <div className="flex items-center justify-between" data-id="element-6637">
                      <div data-id="element-6638">
                        <span className="text-sm font-medium text-gray-900" data-id="element-6639">
                          {c.name}
                        </span>
                        <span className="text-xs text-gray-500 ml-2" data-id="element-6640">
                          {c.learnerIds.length} leerlingen
                        </span>
                      </div>
                      {c.joinCode && <button onClick={() => {
                navigator.clipboard.writeText(c.joinCode || '');
              }} className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded border border-indigo-200 hover:bg-indigo-100 transition-colors flex items-center gap-1" title="Klik om koppelcode te kopiëren" data-id="element-6641">
                          <Copy className="w-3 h-3" data-id="element-6642" />
                          {c.joinCode}
                        </button>}
                    </div>
                  </div>)}
              </div>
            </div>;
    })()}

      {/* Learner Classroom Membership */}
      {user.role === 'learner' && accounts && (() => {
      const classroomIds = accounts.relationships.learnerToClassrooms?.[user.id] || [];
      const classrooms = classroomIds.map(id => accounts.classrooms?.[id]).filter(Boolean);
      if (classrooms.length === 0) return null;
      return <div className="bg-violet-50 rounded-xl p-4 border border-violet-100" data-id="element-6643">
              <h3 className="text-sm font-semibold text-violet-900 flex items-center gap-1.5 mb-2" data-id="element-6644">
                <Users className="w-3.5 h-3.5" data-id="element-6645" /> Klas
              </h3>
              <div className="space-y-1.5" data-id="element-6646">
                {classrooms.map(c => c && <div key={c.id} className="bg-white rounded-lg px-3 py-2 border border-violet-200 flex items-center justify-between" data-id="element-6647">
                        <span className="text-sm font-medium text-gray-900" data-id="element-6648">
                          {c.name}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-6649">
                          {c.learnerIds.length} klasgenoten
                        </span>
                      </div>)}
              </div>
            </div>;
    })()}

      {/* Link Code for learners */}
      {user.role === 'learner' && user.account && isLearnerAccount(user.account) && <div className="bg-blue-50 rounded-xl p-4 border border-blue-100" data-id="element-6650">
            <div className="flex items-center justify-between mb-2" data-id="element-6651">
              <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-1.5" data-id="element-6652">
                <LinkIcon className="w-3.5 h-3.5" data-id="element-6653" /> Koppelcode
              </h3>
              <div className="flex gap-1.5" data-id="element-6654">
                {user.account.linkCode && <button onClick={() => {
            navigator.clipboard.writeText(user.account && isLearnerAccount(user.account) ? user.account.linkCode || '' : '');
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
          }} className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-6655">
                    <Copy className="w-3 h-3" data-id="element-6656" />
                    {codeCopied ? 'Gekopieerd!' : 'Kopieer'}
                  </button>}
                <button onClick={() => {
            const newCode = generateUniqueLinkCode(accounts);
            store.setLearnerLinkCode(user.id, newCode);
            onUserUpdate(prev => {
              if (!prev || !prev.account) return prev;
              return {
                ...prev,
                account: {
                  ...prev.account,
                  linkCode: newCode
                } as Account
              };
            });
          }} className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-6657">
                  <RefreshCw className="w-3 h-3" data-id="element-6658" />
                  Nieuwe code
                </button>
              </div>
            </div>
            {user.account.linkCode ? <div className="font-mono font-black text-xl text-blue-700 tracking-widest bg-white rounded-lg px-4 py-2 text-center border border-blue-200" data-id="element-6659">
                {user.account.linkCode}
              </div> : <p className="text-xs text-blue-600" data-id="element-6660">
                Geen koppelcode. Klik "Nieuwe code" om er een te genereren.
              </p>}
          </div>}

      {/* Mastery by subject for learners */}
      {user.role === 'learner' && selectedMastery.length > 0 && <div data-id="element-6661">
          <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-6662">
            Beheersing per vak
          </h3>
          <div className="space-y-2.5" data-id="element-6663">
            {selectedMastery.map(s => <div key={s.subjectId} data-id="element-6664">
                <div className="flex justify-between text-xs mb-1" data-id="element-6665">
                  <span className="text-gray-600" data-id="element-6666">{s.label}</span>
                  <span className="text-gray-500" data-id="element-6667">
                    {s.count} skills · {s.avg}%
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-6668">
                  <div className={`h-2 rounded-full ${getMasteryBand(s.avg).barClass}`} style={{
              width: `${s.avg}%`
            }} data-id="element-6669" />
                </div>
              </div>)}
          </div>
        </div>}

      {/* Actions */}
      <div className="pt-4 border-t border-gray-100 space-y-2" data-id="element-6670">
        <div className="flex gap-2" data-id="element-6671">
          <Button size="sm" variant="secondary" className="flex-1" onClick={() => onStartEdit(user)} data-id="element-6672">
            <Edit2 className="w-3.5 h-3.5 mr-1.5" data-id="element-6673" /> Bewerken
          </Button>
          <Button size="sm" variant="danger" className="flex-1" onClick={() => setDeleteConfirmOpen(true)} data-id="element-6674">
            <PowerOff className="w-3.5 h-3.5 mr-1.5" data-id="element-6675" /> Verwijderen
          </Button>
        </div>

        {user.role === 'learner' && onNavigateToLearner && <Button size="sm" variant="secondary" className="w-full" onClick={() => {
        onNavigateToLearner(user.id);
        onClose();
      }} data-id="element-6676">
            <Eye className="w-3.5 h-3.5 mr-1.5" data-id="element-6677" /> Dashboard
          </Button>}

        {user.role === 'learner' && <div className="pt-2 border-t border-gray-100" data-id="element-6678">
            {!resetConfirm ? <button onClick={() => setResetConfirm(true)} className="w-full px-4 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors" data-id="element-6679">
                <RotateCcw className="w-3.5 h-3.5 inline mr-1.5" data-id="element-6680" />
                Voortgang resetten
              </button> : <div className="p-3 bg-red-50 border border-red-200 rounded-xl space-y-2" data-id="element-6681">
                <p className="text-xs font-semibold text-red-800" data-id="element-6682">
                  ⚠️ Weet je het zeker? Dit kan niet ongedaan worden gemaakt.
                </p>
                <div className="flex gap-2" data-id="element-6683">
                  <button onClick={() => setResetConfirm(false)} className="flex-1 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-6684">
                    Annuleren
                  </button>
                  <button onClick={() => {
              store.resetLearnerProgress(user.id);
              setResetConfirm(false);
            }} className="flex-1 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700" data-id="element-6685">
                    Definitief resetten
                  </button>
                </div>
              </div>}
          </div>}
      </div>

      <ConfirmDialog open={deleteConfirmOpen} title="Account verwijderen" message={`Weet je zeker dat je ${user.name} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      store.deleteAccount(user.id);
      setDeleteConfirmOpen(false);
      onClose();
    }} onCancel={() => setDeleteConfirmOpen(false)} data-id="element-6686" />
    </div>;
}